import { color } from '@my/util'
import { YStack, XStack, SimpleText, GroupedBarLineChart } from '@my/ui'
import { ToggleButtonGroup } from '../../../../components/index'
import { shadowGenerator } from '../../../../utils/commonStyles'
import { transformApiResponse, valueToMonthData } from '../../helpers'

export type CashFlowTimePeriods = /* '1 Month' |  */'3 Months' | '6 Months' | '9 Months';

interface CashFlowAnalysisProps {
  data: Array<any> // Array of raw financial data (from the API)
  activePeriod: TimePeriods // Selected period for filtering data
  onPeriodChange: (period: TimePeriods) => void // Function to change the active period
}

// CashFlowAnalysis Component
export const CashFlowAnalysis = ({ data, activePeriod, onPeriodChange }: CashFlowAnalysisProps) => {
  // Transform the API data into chart-compatible format
  //const { barData, lineData } = transformApiResponse(data)
  const monthsArray = [0,1,2,3,4,5,6,7,8].slice(0, data.length);
  const barChartData = [
    {
      name: 'Forecasted Income',
      data: valueToMonthData((idx) => parseFloat(data[idx]?.monthlyIncome || 0), monthsArray),
    },
    {
      name: 'Forecasted Expenses',
      data: valueToMonthData((idx) => parseFloat(data[idx]?.spendingThisMonth || 0), monthsArray),
    },
  ];
  const lineChartData = valueToMonthData((idx) => parseFloat(data[idx]?.availableCash || 0), monthsArray)
  .map((monthObj) => ({ ...monthObj, name: 'Forecasted Cash Balance'}));
  return (
    <YStack
      gap={10}
      padding={10}
      backgroundColor={color.white}
      borderRadius={10}
      $platform-web={{ maxWidth: 600 }} // Limit max width for web platform
      {...shadowGenerator(0.3)}
    >
      {/* Header section with title and period toggle */}
      <XStack justifyContent="space-between" flexWrap="wrap" alignItems="center">
        <SimpleText
          fontWeight="700"
          $platform-native={{ fontSize: 14, color: 'black' }}
          $platform-web={{ fontSize: 19, color: color.titleBlack }}
        >
          Your Money Forecast
        </SimpleText>

        {/* Period toggle button group for selecting between '1 Month', '3 Months', and '6 Months' */}
        <ToggleButtonGroup
          isSmall
          active={activePeriod}
          onChange={onPeriodChange}
          options={['3 Months', '6 Months', '9 Months']}
        />
      </XStack>
        <SimpleText
          fontWeight="400"
          color={'black'}
          $platform-native={{ fontSize: 12 }}
          $platform-web={{ fontSize: 14 }}
        >
          Based on your spending patterns and income, here's where your money is headed:
        </SimpleText>
      {/* Display stacked bar and line charts */}
      <GroupedBarLineChart
        lineData={lineChartData}
        barData={barChartData}
        legendData={[
          { name: 'Forecasted Income', fill: color.cyan },
          { name: 'Forecasted Expenses', fill: color.lightRed },
          { name: 'Forecasted Cash Balance', fill: '#495057' },
        ]}
        colorScale={[color.cyan, color.lightRed]}
      />
    </YStack>
  )
}
